// ─────────────────────────────────────────────────────────────────────────────
// WINDOW & DOOR — industry pack 1 (2026-10-18).
//
// Everything a new Window & Door company starts with, in one file: its job
// types (stages with categories, document folders), lead sources, cost
// categories, custom fields, the price book it is seeded with, the features it
// starts with hidden (none), and the words its first-run screens use.
//
// The job types, lead sources and cost categories MOVED HERE VERBATIM from
// jobTypesUtils.js, leadSourcesUtils.js and costCategoriesUtils.js, which now
// hold only the machinery every industry shares. The price book's seed stays
// in priceBookUtils.js beside the model it is built from.
// scripts/checkIndustryPacks.js proves the seed identical to the code it
// replaced.
//
// Registered in utils/industryRegistry.js — read it through the registry
// (buildDefaultJobTypesForIndustry and friends), never from here directly.
// ─────────────────────────────────────────────────────────────────────────────

import { stage, buildStages } from './packBuilders';
import { buildDefaultPriceBook } from '../priceBookUtils';

export const WINDOW_DOOR_INDUSTRY_ID = 'windowDoor';

// A window: outer frame with a cross mullion. Reads instantly as "window &
// door" without any literal illustration. See IndustryIcon for the drawing.
const WINDOW_DOOR_ICON = {
  viewBox: '0 0 24 24',
  paths: [
    'M3 3 H21 V21 H3 Z',
    'M12 3 V21',
    'M3 12 H21',
  ],
};

// The 5 shipped job types. Called at seed time so each company gets its own
// stage IDs.
export function buildWindowDoorJobTypes() {
  return [
    {
      id: 'replacement',
      name: 'Replacement',
      isDefault: true,
      basedOn: null,
      defaultStages: buildStages([
        stage('Lead', 'Lead'),
        stage('Proposal Sent', 'Sent'),
        stage('Contract Awarded', 'Awarded'),
        stage('Documents Finalized & Deposit Received', 'In Progress'),
        stage('Ready to Schedule', 'In Progress'),
        stage('Scheduled / In-Progress', 'In Progress'),
        stage('Work Complete / Money Due', 'In Progress'),
        stage('Paid & Complete', 'Completed'),
        // Lost stages — hidden from the kanban (Lost category), available for
        // selection when a deal falls through.
        stage('Lost - No Budget', 'Lost'),
        stage('Lost - Chose Competitor', 'Lost'),
        stage('Lost - No Response', 'Lost'),
      ]),
      defaultFolders: ['Proposals & Contracts', 'Permits', 'Final Measure', 'Install Photos', 'Invoices'],
      defaultTasks: [],
      defaultPricingPresetId: null,
      order: 0,
    },
    {
      id: 'newConstruction',
      name: 'New Construction',
      isDefault: true,
      basedOn: null,
      defaultStages: buildStages([
        stage('Lead', 'Lead'),
        stage('Proposal Sent', 'Sent'),
        stage('Contract Awarded', 'Awarded'),
        stage('Plans & Permits', 'In Progress'),
        stage('Materials Ordered', 'In Progress'),
        stage('Ready to Schedule', 'In Progress'),
        stage('Scheduled / In-Progress', 'In Progress'),
        stage('Work Complete / Money Due', 'In Progress'),
        stage('Paid & Complete', 'Completed'),
      ]),
      defaultFolders: ['Proposals & Contracts', 'Plans & Submittals', 'Permits', 'NOAs', 'Install Photos', 'Invoices'],
      defaultTasks: [],
      defaultPricingPresetId: null,
      order: 1,
    },
    {
      id: 'materialsOnly',
      name: 'Materials Only',
      isDefault: true,
      basedOn: null,
      defaultStages: buildStages([
        stage('Quote Requested', 'Lead'),
        stage('Quote Sent', 'Sent'),
        stage('Order Confirmed', 'Awarded'),
        stage('Materials Ordered', 'In Progress'),
        stage('Ready for Pickup / Delivery', 'In Progress'),
        stage('Fulfilled', 'Completed'),
      ]),
      defaultFolders: ['Quotes', 'Order Confirmations', 'Invoices'],
      defaultTasks: [],
      defaultPricingPresetId: null,
      order: 2,
    },
    {
      id: 'service',
      name: 'Service',
      isDefault: true,
      basedOn: null,
      defaultStages: buildStages([
        stage('Service Request', 'Lead'),
        stage('Scheduled', 'In Progress'),
        stage('In Progress', 'In Progress'),
        stage('Complete / Invoice Sent', 'In Progress'),
        stage('Paid', 'Completed'),
      ]),
      defaultFolders: ['Service Reports', 'Invoices'],
      defaultTasks: [],
      defaultPricingPresetId: null,
      order: 3,
    },
    {
      id: 'other',
      name: 'Other',
      isDefault: true,
      basedOn: null,
      defaultStages: buildStages([
        stage('Lead', 'Lead'),
        stage('In Progress', 'In Progress'),
        stage('Complete', 'Completed'),
      ]),
      defaultFolders: ['Documents'],
      defaultTasks: [],
      defaultPricingPresetId: null,
      order: 4,
    },
  ];
}

// The eight shipped lead sources. STABLE string ids, so read-time seeding
// (which can run on any client, including non-owners that can't persist)
// produces identical ids everywhere. See leadSourcesUtils.js for the model.
export function buildWindowDoorLeadSources() {
  return [
    { id: 'referral', name: 'Referral', isActive: true, order: 0 },
    { id: 'google', name: 'Google', isActive: true, order: 1 },
    { id: 'door_knock', name: 'Door Knock', isActive: true, order: 2 },
    { id: 'repeat_customer', name: 'Repeat Customer', isActive: true, order: 3 },
    { id: 'social_media', name: 'Social Media', isActive: true, order: 4 },
    { id: 'yard_sign', name: 'Yard Sign', isActive: true, order: 5 },
    { id: 'home_show', name: 'Home Show', isActive: true, order: 6 },
    { id: 'other', name: 'Other', isActive: true, order: 7 },
  ];
}

// The five shipped cost categories, with STABLE string ids for the same reason
// — a cost entry created against 'labor' resolves the same regardless of who
// seeded. See costCategoriesUtils.js for the model.
export function buildWindowDoorCostCategories() {
  return [
    { id: 'labor', name: 'Labor', isActive: true, order: 0 },
    { id: 'materials', name: 'Materials', isActive: true, order: 1 },
    { id: 'subcontractor', name: 'Subcontractor', isActive: true, order: 2 },
    { id: 'equipment', name: 'Equipment', isActive: true, order: 3 },
    { id: 'permit', name: 'Permit', isActive: true, order: 4 },
  ];
}

export const WINDOW_DOOR_PACK = {
  id: WINDOW_DOOR_INDUSTRY_ID,
  name: 'Window & Door Companies',
  isPublic: true,
  icon: WINDOW_DOOR_ICON,
  buildDefaultJobTypes: buildWindowDoorJobTypes,
  buildDefaultLeadSources: buildWindowDoorLeadSources,
  buildDefaultCostCategories: buildWindowDoorCostCategories,
  // Window & Door ships no custom fields — owners add their own in Settings.
  buildDefaultCustomFields: () => ({ jobs: [], clients: [], serviceTickets: [] }),
  // The PGT / ES / CWS manufacturer book (priceBookUtils.js).
  buildDefaultPriceBook,
  // Every optional feature starts visible.
  hiddenFeatures: [],
  // The words the first-run screens use, exactly as they read before packs.
  copy: {
    companyNamePlaceholder: 'e.g. Coastal Impact Windows',
    pipelineIntro: 'We’ve built these from how window and door companies usually work.',
    jobTypeConcept: 'A kind of work you sell. Each job type carries its own set of stages, because a full replacement and a service call do not move through your business the same way.',
    jobNamePlaceholder: 'e.g. Johnson — Master Bedroom Windows',
    // Settings. Moved here verbatim from Settings.js, JobTypeEditor.js and
    // userService.js's PERMISSION_LABELS (2026-10-18).
    jobTypesIntro: 'Each job type ships its own stages, document folders, tasks, and pricing preset — so a new Replacement is set up differently than a Service call.',
    jobTypeNamePlaceholder: 'e.g. Warranty Callback, Commercial New Construction',
    stageNamePlaceholder: 'e.g. Lead, Proposal Sent, Scheduled',
    folderNamePlaceholder: 'e.g. Proposals & Contracts, Permits, Install Photos',
    permissionDescs: {
      managePricingOptions: 'Edits the dropdown lists every quote inherits — frame type, glass, trim. Affects all future quotes.',
      showInternalCosts: 'Install rates, material costs, trim rates and markup on the Pricing tab. Turn off and they see the client price only.',
    },
  },
};
